'use client';

import { useState, useEffect, useMemo, Suspense } from 'react';
import Link from 'next/link';
import { LegalConsentCheckbox } from '@/components/legal/LegalConsentCheckbox';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EnthiosHubLoader } from '@/components/ui/EnthiosHubLoader';
import { useAuthStore } from '@/lib/auth-store';
import { pricingApi, getApiErrorMessage } from '@/lib/api';
import { canBecomeOrganizer } from '@/lib/rbac';
import { resolveDashboardForUser } from '@/lib/role-dashboard';

const STEPS = [
  { id: 1, title: 'Account', key: 'account' },
  { id: 2, title: 'Organization', key: 'organization' },
  { id: 3, title: 'Plan', key: 'plan' },
  { id: 4, title: 'Confirmation', key: 'confirmation' },
];

const ORG_TYPES = [
  'Corporate',
  'College / University',
  'Community',
  'Startup',
  'Other',
];

function BecomeOrganizerContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const planParam = searchParams?.get('plan') ?? null;

  const { user, isAuthenticated, authChecking, _hasHydrated, checkAuth, becomeOrganizer, isLoading } = useAuthStore();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plansError, setPlansError] = useState('');
  const [plansLoaded, setPlansLoaded] = useState(false);

  const [form, setForm] = useState({
    company_name: '',
    company_description: '',
    website: '',
    phone: '',
    organization_type: '',
    city: '',
    country: '',
  });
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const dashboardUrl = resolveDashboardForUser(user);
  useEffect(() => {
    if (!_hasHydrated || authChecking) return;
    if (!isAuthenticated) {
      router.replace('/login?redirect=create-event');
      return;
    }
    if (!canBecomeOrganizer(user)) {
      if (planParam) {
        router.replace(`/dashboard/settings/subscription?plan=${encodeURIComponent(planParam)}`);
      } else {
        router.replace('/dashboard/settings/subscription');
      }
    }
  }, [_hasHydrated, authChecking, isAuthenticated, user, router, dashboardUrl, planParam]);

  useEffect(() => {
    pricingApi.plans().then((res) => {
      const data = res.data?.data ?? res.data;
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setPlans(list);
      if (list.length > 0 && !selectedPlanId) {
        const queryMatch = planParam
          ? list.find((p: any) =>
              String(p.key || p.slug || p.id || p.name || '').toLowerCase() === planParam.toLowerCase()
            )
          : null;
        const defaultPlan = queryMatch || list.find((p: any) => (p.key || p.slug || p.name || '').toLowerCase().includes('free')) || list[0];
        setSelectedPlanId(defaultPlan.id || defaultPlan.key);
      }
    }).catch(() => setPlansError('Could not load plans. Refresh the page or try again later.'))
      .finally(() => setPlansLoaded(true));
  }, [planParam]);

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId || plans.length === 0) return null;
    return (
      plans.find(
        (p: { id?: string; key?: string }) =>
          String(p?.id ?? '') === String(selectedPlanId) || String(p?.key ?? '') === String(selectedPlanId)
      ) ?? null
    );
  }, [plans, selectedPlanId]);

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const canProceed = () => {
    if (step === 2) {
      return form.company_name.trim().length > 0;
    }
    if (step === 3) {
      return Boolean(selectedPlan && selectedPlanId);
    }
    if (step === 4) {
      return agreedToTerms;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step < 4) setStep(step + 1);
    else handleSubmit();
  };

  const handleBack = () => {
    setError('');
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (loading || isLoading) return;
    if (!agreedToTerms) {
      setError('Please agree to the Terms and Privacy Policy.');
      return;
    }
    if (!selectedPlanId || !plansLoaded) {
      setError('Select a billing plan before continuing.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await becomeOrganizer({
        organization_name: form.company_name.trim(),
        company_name: form.company_name.trim(),
        company_description: form.company_description.trim() || undefined,
        organization_type: form.organization_type || undefined,
        website: form.website.trim() || undefined,
        phone: form.phone.trim() || undefined,
        city: form.city.trim() || undefined,
        country: form.country.trim() || undefined,
        plan_id: selectedPlanId,
        billing_cycle: 'monthly',
      });
      await checkAuth();
      router.push(dashboardUrl);
    } catch (err: any) {
      const data = err?.response?.data;
      let msg =
        typeof data?.errors === 'object' && data.errors !== null
          ? (Object.values(data.errors).flat().find((m) => typeof m === 'string' && String(m).trim()) as string | undefined)
          : undefined;
      if (!msg) {
        msg = typeof data?.message === 'string' ? data.message : undefined;
      }
      setError(msg || getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!_hasHydrated || authChecking || !isAuthenticated || !canBecomeOrganizer(user)) {
    return (
      <div className="min-h-screen app-shell flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen app-shell flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl">
        <Link href="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-text-inverse">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="text-xl font-display font-bold text-nature-text">EnthiosHub</span>
        </Link>

        <div className="glass-card p-6 md:p-8">
          <div className="mb-6">
            <p className="text-xs font-semibold text-nature-muted uppercase tracking-wide">
              Step {step} of 4
            </p>
            <div className="mt-2 h-1.5 w-full bg-nature-border rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={false}
                animate={{ width: `${(step / 4) * 100}%` }}
                transition={{ duration: 0.25 }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px] font-medium text-nature-muted">
              {STEPS.map((s) => (
                <span key={s.id} className={step >= s.id ? 'text-primary' : ''}>
                  {s.title}
                </span>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-display font-semibold text-nature-text">
                  Create Your Organizer Account
                </h2>
                <p className="text-sm text-nature-muted">
                  You’re signed in as <strong>{user?.email}</strong>. Continue to add your organization details.
                </p>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-display font-semibold text-nature-text">
                  Tell Us About Your Organization
                </h2>
                <Input
                  label="Organization Name"
                  value={form.company_name}
                  onChange={(e) => updateForm('company_name', e.target.value)}
                  placeholder="Acme Events Inc."
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-nature-text mb-1.5">
                    Organization Type
                  </label>
                  <select
                    value={form.organization_type}
                    onChange={(e) => updateForm('organization_type', e.target.value)}
                    className="w-full rounded-[10px] border border-nature-border bg-bg px-4 py-3 text-sm text-nature-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  >
                    <option value="">Select type</option>
                    {ORG_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Website (optional)"
                  type="url"
                  value={form.website}
                  onChange={(e) => updateForm('website', e.target.value)}
                  placeholder="https://"
                />
                <Input
                  label="Phone (optional)"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="+91 12345 67890"
                />
                <Input
                  label="City / Country (optional)"
                  value={[form.city, form.country].filter(Boolean).join(', ')}
                  onChange={(e) => {
                    const v = e.target.value;
                    const [city, ...rest] = v.split(',').map((s) => s.trim());
                    updateForm('city', city || '');
                    updateForm('country', rest.join(', ') || '');
                  }}
                  placeholder="City, Country"
                />
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-display font-semibold text-nature-text">
                  Choose a Plan
                </h2>
                <p className="text-sm text-nature-muted">
                  You can change your plan later from the dashboard.
                </p>
                <div className="grid gap-3">
                  {plansError && (
                    <p className="text-sm text-red-600">{plansError}</p>
                  )}
                  {plans.length === 0 ? (
                    <div className="rounded-xl border border-nature-border p-4 text-center text-nature-muted text-sm">
                      {plansLoaded ? 'No plans available. Please try again later.' : 'Loading plans…'}
                    </div>
                  ) : (
                    plans.map((plan: any) => {
                      const id = plan.id || plan.key;
                      const isSelected = selectedPlanId === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSelectedPlanId(id)}
                          className={`rounded-xl border p-4 text-left transition ${
                            isSelected
                              ? 'border-2 border-primary bg-primary/5'
                              : 'border-nature-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-nature-text">{plan.name}</p>
                              <p className="text-sm text-nature-muted mt-1">
                                {plan.commission_percent != null
                                  ? `${Number(plan.commission_percent)}% platform fee per ticket`
                                  : plan.price_monthly != null
                                    ? `₹${Number(plan.price_monthly).toLocaleString()}/mo`
                                    : plan.price_yearly != null
                                      ? `₹${Number(plan.price_yearly).toLocaleString()}/yr`
                                      : 'Platform fee-based pricing'}
                              </p>
                            </div>
                            {isSelected && (
                              <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-nature-muted mt-2">{plan.description}</p>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-display font-semibold text-nature-text">
                  Review Your Organizer Setup
                </h2>
                <div className="rounded-xl border border-nature-border bg-nature-bg/50 p-4 space-y-2 text-sm">
                  <p><span className="text-nature-muted">Name</span> <span className="font-medium text-nature-text">{user?.name || user?.email}</span></p>
                  <p><span className="text-nature-muted">Organization</span> <span className="font-medium text-nature-text">{form.company_name || '—'}</span></p>
                  <p><span className="text-nature-muted">Plan</span> <span className="font-medium text-nature-text">{selectedPlan?.name ?? selectedPlan?.plan_name ?? '—'}</span></p>
                </div>
                <LegalConsentCheckbox
                  context="organizer_registration"
                  checked={agreedToTerms}
                  onChange={setAgreedToTerms}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {error && (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            {step === 1 ? (
              <Link
                href="/dashboard"
                className="inline-flex items-center gap-1.5 rounded-xl border border-nature-border px-4 py-2 text-sm font-medium text-nature-muted hover:bg-nature-bg"
              >
                Cancel
              </Link>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading || isLoading}
                leftIcon={<ArrowLeft className="w-4 h-4" />}
              >
                Back
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              disabled={!canProceed() || loading || isLoading || (step === 3 && plans.length === 0)}
              loading={(loading || isLoading) && step === 4}
              rightIcon={step < 4 ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {step === 4 ? 'Start Hosting Events' : 'Continue'}
            </Button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-nature-muted">
          Already an organizer? <Link href="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default function BecomeOrganizerPage() {
  return (
    <Suspense fallback={<EnthiosHubLoader variant="fullscreen" showDelayMs={0} />}>
      <BecomeOrganizerContent />
    </Suspense>
  );
}
